import { useMemo } from 'react';
import cx from 'classnames';
import { useParams } from 'react-router';

import { useOrders } from 'app/context';
import { COLOR, CREDIT_CARD, TEXT } from 'common/const';
import {
  AddressDisplay,
  BoothSetup,
  Content,
  EventDates,
  OrderSummary,
  PaymentMethod,
  ProductList,
  Requirement,
} from 'common/components';
import { useCatalogCategoryNames, useEvent } from 'common/hooks';
import { date, DATE_FORMAT, decoratePhone } from 'common/utils';

import { COMPLETED } from '../const';
import { OrderItem, SectionTitle } from '../components';

import styles from './OrderInvoice.module.css';
import { Page } from 'common/site';

const { DATE_LABEL } = COMPLETED;

const OrderInvoice = () => {
  const { id } = useParams();
  // For getting data from EMP leaving out for now
  // const { empOrderId } = useParams();
  const {
    isLoading: isLoadingOrders,
    error: ordersError,
    findOrder,
  } = useOrders();

  const {
    billing,
    booth,
    eventId,
    id: ooId,
    personal,
    company,
    ...order
  } = useMemo(() => {
    const order = findOrder(id) ?? {};

    const personal = order?.personalInformation ?? {};
    const billing = order?.billingInformation ?? {};
    const company = order?.company ?? '';
    const { eventId, booth } = order;

    return {
      ...order,
      billing,
      booth,
      eventId,
      personal,

      company,
    };
  }, [findOrder, id]);

  // const {
  //   data: statementData,
  //   isLoading: loadingStatement,
  //   error: statmentError,
  // } = useQuery(
  //   ['getStatementData', { eventId, empOrderId }],
  //   () => getInvoiceDocumentData({ eventId, empOrderId }),
  //   { enabled: Boolean(eventId) },
  // );

  const boothWork = useMemo(
    () =>
      order.assessmentResponseData?.filter(
        ({ assessmentId: id }) => id > 4 && id !== 12,
      ) ?? [],
    [order.assessmentResponseData],
  );

    const { data: event = {}, isLoading: isEventLoading } = useEvent(eventId);

  const card = useMemo(() => {
    return Object.values(CREDIT_CARD).find(
      ({ db }) => db === order.payment?.type,
    );
  }, [order.payment?.type]);

  // TODO: Remove if we are getting data from EMP and update statementData
  const { categories, isCatalogNamesLoading, categoryNamesError } =
    useCatalogCategoryNames({
      ooId: ooId,
      eventId,
      boothLocation: booth.location,
      boothType: booth.type,
      items: order.items,
    });

  const error = useMemo(
    () => categoryNamesError?.message ?? ordersError?.message,
    [categoryNamesError, ordersError],
  );

  const isLoading = useMemo(
    () => isLoadingOrders || isEventLoading || isCatalogNamesLoading,
    [isLoadingOrders, isEventLoading, isCatalogNamesLoading],
  );

  return (
    <Page variant="print" className={TEXT.BODY_2} title="Order Statement">
      <Content error={error} isLoading={isLoading}>
        <header className={styles.section}>
          <h1 className={cx(TEXT.H4, styles.title)}>Order Statement</h1>

          <ul className={styles.details}>
            <li>
              {DATE_LABEL}: {date(order.date).format(DATE_FORMAT.MEDIUM_DATE)}
            </li>

            <li>Order ID: {ooId}</li>
          </ul>
        </header>

        <div className={styles.details}>
          <div className={styles.section}>
            <SectionTitle>Payment</SectionTitle>
            <PaymentMethod
              method={order.payment?.method}
              last4={order.payment?.last4}
              card={card}
            />
          </div>

          <div className={styles.section}>
            {order.totals && (
              <OrderSummary
                totals={order.totals}
                paymentMethod={order.payment?.method}
                eventId={eventId}
                showBalance={true}
                amountPaid={order.payment?.amountPaid}
              />
            )}
          </div>
        </div>

        <div className={styles.details}>
          <section className={styles.section}>
            <div className={styles.section}>
              <SectionTitle>{order.eventName}</SectionTitle>
              <ol>
                <li>
                  <EventDates start={event.dateStart} end={event.dateEnd} />
                </li>
                <li>{event.facilityName}</li>
                <li>{event.facilityLocation}</li>
              </ol>
            </div>

            <div className={styles.section}>
              <SectionTitle>Booth Setup</SectionTitle>
              <BoothSetup booth={booth} showNumber />
            </div>
          </section>

          {boothWork.length ? (
            <section className={styles.section}>
              <SectionTitle>Booth Work</SectionTitle>
              <ul>
                {boothWork.map(({ assessmentId, response, question }) => (
                  <li key={assessmentId} className={styles.requirement}>
                    <Requirement
                      textFont={TEXT.BODY_1}
                      isMet={response === 'Yes'}
                    >
                      {question}
                    </Requirement>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <div className={styles.details}>
          <div className={styles.section}>
            <section className={styles.section}>
              <SectionTitle>Personal Information</SectionTitle>
              <p>
                {personal.firstName} {personal.lastName}
              </p>
              <p>{personal.email}</p>
              <p>{decoratePhone(personal.mobile)}</p>
            </section>

            {order.isThirdParty && (
              <section>
                <SectionTitle>Exhibitor Information</SectionTitle>
                <p>{order.thirdPartyCompanyName}</p>
              </section>
            )}
          </div>

          <section className={styles.section}>
            <SectionTitle>{company} Information</SectionTitle>
            <AddressDisplay address={billing} displayPhone />
          </section>
        </div>

        <section className={cx(styles.section)}>
          <SectionTitle>Your Items</SectionTitle>
          <ProductList
            categories={categories}
            items={order.items}
            renderItem={(item) => <OrderItem item={item} variant="invoice" />}
          />
          {/* Uncomment for EMP item list */}
          {/* <ul>
            {statementData?.items.map((item) =>
              item.isPackage || item.isKeyword ? (
                <li
                  className={cx(TEXT.BOLD, styles.package)}
                  key={item.name + item.id}
                >
                  {item.name}
                </li>
              ) : (
                <li key={item.name + item.id}>
                  <OrderItem item={item} variant="invoice" />
                </li>
              ),
            )}
          </ul> */}
        </section>

        <section className={styles.section}>
          <SectionTitle>Branch Information</SectionTitle>
          <div className={styles.details}>
            <ul>
              <li>{event.branchInfo?.name}</li>
              <li>{event.branchInfo?.email}</li>
            </ul>
            <AddressDisplay address={event.branchInfo} displayPhone />
          </div>
        </section>

        <p className={cx(COLOR.TEXT_SECONDARY, styles.disclaimer)}>
          This is an account summary/order confirmation and is not a final
          invoice. This statement is subject to change based upon services
          rendered.
        </p>
      </Content>
    </Page>
  );
};

export default OrderInvoice;
